import React from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useSweepstakesData } from '../../../../contexts';
import { RenderSweepstakesDrawerLoading, RenderGenericError } from '../SweepstakesUtils';
import { useImageError, handleHeroImageError } from '../../utils/SweepstakesUtils';
import { SWEEPSTAKES_CONSTANTS } from '../../../../core/Constants';

// Render components
const RenderHeroImage = ({ heroImage }) => {
  if (!heroImage) return null;

  return (
    <div className="sui-mb-6">
      <img
        src={heroImage}
        alt="Hero"
        className="sui-w-full sui-rounded-lg"
        width="957"
        height="262"
        onError={handleHeroImageError}
      />
    </div>
  );
};

const RenderLogo = ({ logoUrl, logo }) => {
  if (!logoUrl) return null;

  return logo.hasError ? (
    <img
      src={SWEEPSTAKES_CONSTANTS.URLS.LOGO_FALLBACK}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      data-testid="sweepstakes-drawer-logo-fallback"
    />
  ) : (
    <img
      src={logoUrl}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      loading="lazy"
      onError={logo.handleError}
      data-testid="sweepstakes-drawer-logo"
    />
  );
};

const RenderSweepstakesTitle = ({ sweepstakesTitle, logoUrl, logo }) => {
  if (!logoUrl && !sweepstakesTitle) return null;

  return (
    <div className="sui-flex sui-items-center sui-gap-3 sui-mb-4">
      <RenderLogo logoUrl={logoUrl} logo={logo} />
      {sweepstakesTitle && (
        <Typography variant="h2" weight="display" height="tight">{sweepstakesTitle}</Typography>
      )}
    </div>
  );
};

export const SweepstakesDrawer = ({
  isOpen,
  onClose,
  onBack,
  drawerTitle,
  sweepstakesTitle,
  logoUrl,
  heroImage,
  children,
  footerContent,
  isLoading,
  hasError,
  testId,
}) => {
  const logo = useImageError();
  const { refresh } = useSweepstakesData();

  if (!isOpen) return null;

  return (
    <Drawer open={isOpen} onClose={onClose} data-testid="sweepstakes-drawer">
      <DrawerHeader
        title={drawerTitle}
        onClose={onClose}
        onBack={onBack}
        data-testid={testId}
      />
      <DrawerBody>
        {isLoading && <RenderSweepstakesDrawerLoading />}
        {!isLoading && hasError && <RenderGenericError refresh={refresh} />}
        {!isLoading && !hasError && (
          <div className="sui-flex sui-flex-col sui-h-full sui-mt-3">
            <RenderHeroImage heroImage={heroImage} />
            <RenderSweepstakesTitle sweepstakesTitle={sweepstakesTitle} logoUrl={logoUrl} logo={logo} />
            {children}
          </div>
        )}
      </DrawerBody>
      {!isLoading && !hasError && footerContent && (
        <DrawerFooter variant="sticky">
          {footerContent}
        </DrawerFooter>
      )}
    </Drawer>
  );
};

SweepstakesDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  drawerTitle: PropTypes.string,
  sweepstakesTitle: PropTypes.string,
  logoUrl: PropTypes.string,
  heroImage: PropTypes.string,
  children: PropTypes.node,
  footerContent: PropTypes.node,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  testId: PropTypes.string,
};

SweepstakesDrawer.defaultProps = {
  onBack: null,
  drawerTitle: null,
  sweepstakesTitle: null,
  logoUrl: null,
  heroImage: null,
  children: null,
  footerContent: null,
  isLoading: false,
  hasError: false,
  testId: null,
};